import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortableTable } from './hooks/useSortableTable';
import { apiFetch } from './utils/api';

function SitePurchaseOrdersTab({ siteId, siteName, customerId, customerName }) {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Sorting
  const { items: sortedPOs, requestSort, getSortIndicator } = useSortableTable(purchaseOrders, { key: 'order_date', direction: 'desc' });

  useEffect(() => {
    fetchPurchaseOrders();
  }, [siteId]);

  const fetchPurchaseOrders = async () => {
    try {
      const res = await apiFetch('/api/purchase-orders');
      const allPOs = await res.json();
      
      // Filter POs for this specific site (direct site_id match)
      const sitePOs = allPOs.filter(po => po.site_id === parseInt(siteId));
      
      setPurchaseOrders(sitePOs);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      received: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const isOverdue = (po) => {
    if (!po.expected_date || po.status === 'received' || po.status === 'cancelled') return false;
    return new Date(po.expected_date) < new Date();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Purchase Orders for This Site</h2>
          <p className="text-gray-600 mt-1">
            {purchaseOrders.length} {purchaseOrders.length === 1 ? 'order' : 'orders'} linked to jobs at {siteName}
          </p>
        </div>
      </div>

      {/* Purchase Orders List */}
      {purchaseOrders.length === 0 ? (
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Purchase Orders for This Site</h3>
          <p className="text-gray-600">
            Purchase orders are created from jobs when materials are needed
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-300 border-b border-gray-400">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('po_number')}>
                  PO #{getSortIndicator('po_number')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('supplier_name')}>
                  Supplier{getSortIndicator('supplier_name')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('job_number')}>
                  Job{getSortIndicator('job_number')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('order_date')}>
                  Order Date{getSortIndicator('order_date')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('expected_date')}>
                  Expected{getSortIndicator('expected_date')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('status')}>
                  Status{getSortIndicator('status')}
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('total_amount')}>
                  Total{getSortIndicator('total_amount')}
                </th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedPOs.map((po, index) => (
                <tr 
                  key={po.id}
                  className={`hover:bg-blue-50 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-50' : 'bg-white'}`}
                  onClick={() => navigate(`/purchase-orders/${po.id}`)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {po.po_number}
                    {isOverdue(po) && (
                      <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded">
                        OVERDUE
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {po.supplier_name || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {po.job_number || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDate(po.order_date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDate(po.expected_date)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(po.status)}`}>
                      {po.status || 'draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                    {formatCurrency(po.total_amount)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/purchase-orders/${po.id}`);
                      }}
                      className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black rounded text-xs font-medium transition"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SitePurchaseOrdersTab;
